// ListView.jsx

import {
  Box,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import React from "react";

// Styled components
const WeekDivider = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.primary.lighter,
}));

const WeekLabel = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  fontWeight: 600,
  color: theme.palette.primary.dark,
  fontSize: "0.8rem",
}));

const DateCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2, 2),
  whiteSpace: "nowrap",
  fontSize: "0.8rem",
}));

const DayNameCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2, 2),
  whiteSpace: "nowrap",
  fontSize: "0.8rem",
}));

const EmptyShiftIndicator = styled(ErrorOutlineIcon)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: "1rem",
  marginLeft: theme.spacing(0.5),
  verticalAlign: "middle",
}));

const ShiftSelect = styled(Select)(({ theme, isAlternate }) => ({
  fontSize: "0.85rem",
  width: "100%",
  backgroundColor: isAlternate
    ? theme.palette.info.light
    : theme.palette.background.paper,
}));

// Helper function to get the day name
const getDayName = (year, month, day) => {
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", { weekday: "short" });
};

// Helper function to group days by week
const groupByWeek = (scheduleData, month, year) => {
  const weeks = [];
  let currentWeek = [];
  let weekNumber = 1;

  const daysInMonth = scheduleData.days.length;
  const firstDayOfMonth = new Date(year, month - 1, 1).getDay();

  // Calculate the day number for the first day of the first full week
  const firstFullWeekDay = 1 + ((7 - firstDayOfMonth) % 7);

  // Group days into weeks
  for (let i = 1; i <= daysInMonth; i++) {
    const dayData = scheduleData.days.find((d) => d.day === i) || {
      day: i,
      shift1: { name: "" },
      shift2: { name: "" },
    };

    currentWeek.push({
      ...dayData,
      dayName: getDayName(year, month, i),
      date: `${month.toString().padStart(2, "0")}/${i
        .toString()
        .padStart(2, "0")}`,
    });

    // If we've reached the end of a week or end of the month, add the week to the array
    if (i % 7 === firstFullWeekDay % 7 || i === daysInMonth) {
      weeks.push({
        weekNumber,
        days: [...currentWeek],
      });
      currentWeek = [];
      weekNumber++;
    }
  }

  return weeks;
};

// ListView Component
const CalendarListView = ({ scheduleData, availableHosts, onUpdateShift }) => {
  // Group days by week
  const weeks = groupByWeek(
    scheduleData,
    scheduleData.month,
    scheduleData.year
  );

  // Handle shift change
  const handleShiftChange = (day, shiftKey, event) => {
    const newValue = event.target.value;

    // Find the existing day data
    const dayData = scheduleData.days.find((d) => d.day === day) || {
      day,
      shift1: { name: "" },
      shift2: { name: "" },
    };

    // Determine if this is an alternate shift (preserve the alternate status if it exists)
    const isAlternate = dayData[shiftKey]?.alternate || false;

    // Call the update function
    onUpdateShift(day, shiftKey, {
      name: newValue,
      alternate: isAlternate,
    });
  };

  // Toggle the alternate status
  const toggleAlternateStatus = (day, shiftKey) => {
    // Find the existing day data
    const dayData = scheduleData.days.find((d) => d.day === day);
    if (!dayData) return;

    // Get current data for this shift
    const currentShift = dayData[shiftKey] || { name: "" };

    // Call the update function with toggled alternate status
    onUpdateShift(day, shiftKey, {
      name: currentShift.name,
      alternate: !currentShift.alternate,
    });
  };

  return (
    <Box sx={{ maxWidth: "100%", overflowX: "auto" }}>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "20%" }}>Date</TableCell>
              <TableCell sx={{ width: "20%" }}>Day</TableCell>
              <TableCell sx={{ width: "30%" }}>Shift 1</TableCell>
              <TableCell sx={{ width: "30%" }}>Shift 2</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {weeks.map((week, weekIndex) => (
              <React.Fragment key={`week-${weekIndex}`}>
                <WeekDivider>
                  <WeekLabel colSpan={4}>
                    --- Week {week.weekNumber} ---
                  </WeekLabel>
                </WeekDivider>

                {week.days.map((day) => (
                  <TableRow key={`day-${day.day}`} hover>
                    <DateCell>{day.date}</DateCell>
                    <DayNameCell>{day.dayName}</DayNameCell>

                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <FormControl fullWidth size="small">
                          <ShiftSelect
                            value={day.shift1?.name || ""}
                            onChange={(e) =>
                              handleShiftChange(day.day, "shift1", e)
                            }
                            displayEmpty
                            isAlternate={day.shift1?.alternate}
                            renderValue={(selected) => {
                              if (!selected) return "Select host";
                              return day.shift1?.alternate
                                ? `${selected}**`
                                : selected;
                            }}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              toggleAlternateStatus(day.day, "shift1");
                            }}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {availableHosts.map((host) => (
                              <MenuItem key={host} value={host}>
                                {host}
                              </MenuItem>
                            ))}
                          </ShiftSelect>
                        </FormControl>

                        {!day.shift1?.name && (
                          <Tooltip title="Empty shift needs to be assigned">
                            <EmptyShiftIndicator sx={{ ml: 1 }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <FormControl fullWidth size="small">
                          <ShiftSelect
                            value={day.shift2?.name || ""}
                            onChange={(e) =>
                              handleShiftChange(day.day, "shift2", e)
                            }
                            displayEmpty
                            isAlternate={day.shift2?.alternate}
                            renderValue={(selected) => {
                              if (!selected) return "Select host";
                              return day.shift2?.alternate
                                ? `${selected}**`
                                : selected;
                            }}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              toggleAlternateStatus(day.day, "shift2");
                            }}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {availableHosts.map((host) => (
                              <MenuItem key={host} value={host}>
                                {host}
                              </MenuItem>
                            ))}
                          </ShiftSelect>
                        </FormControl>

                        {!day.shift2?.name && (
                          <Tooltip title="Empty shift needs to be assigned">
                            <EmptyShiftIndicator sx={{ ml: 1 }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Right-click on a shift to toggle temporary coverage (** status).
        </Typography>
      </Box>
    </Box>
  );
};

export default CalendarListView;
